import { useCallback, useRef } from "react";
import { useMediaQuery } from "usehooks-ts";

/** The glare's colour when lit — a soft white, not a tone; the tone bloom already carries the widget's colour. */
const SPOTLIGHT_LIT = "color-mix(in oklab, white 16%, transparent)";

/**
 * A soft glare that tracks the pointer across a widget card, the way light
 * catches glass. Purely a hover cue for a fine pointer with room to hover —
 * touch has no hover state to react to, so it never engages there.
 *
 * Position and on/off state are both written straight to the DOM via
 * `style.setProperty`, never through React state. The position changes on every
 * `pointermove`; running that through a re-render would mean the whole card
 * subtree re-rendering at pointer-move frequency for a purely cosmetic effect.
 * The CSS reads the two custom properties this sets — `--spotlight-x/-y` for
 * position, `--spotlight-color` for on/off — see `.widget-card-interactive::before`
 * in global.css, which is itself behind a `(hover: hover) and (pointer: fine)`
 * query so a touch device never composites the layer at all.
 */
export function usePointerSpotlight() {
  const isFinePointer = useMediaQuery("(pointer: fine)");

  const elementRef = useRef<HTMLElement | null>(null);

  const attach = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
  }, []);

  const track = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!isFinePointer) return;
      const element = elementRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      element.style.setProperty("--spotlight-x", `${x}%`);
      element.style.setProperty("--spotlight-y", `${y}%`);
      element.style.setProperty("--spotlight-color", SPOTLIGHT_LIT);
    },
    [isFinePointer],
  );

  const clear = useCallback(() => {
    elementRef.current?.style.removeProperty("--spotlight-color");
  }, []);

  return { attach, track, clear };
}
